// Messages between embed.js (the client's page) and the chat frame
// (widget.getunitivo.com). Both sides check the other's origin and window
// before reading anything, and never post to "*".

export const NS = "unitivo";

/** How the page shows the frame: a panel, or the whole screen on phones (with the notch insets). */
export interface Layout {
  fullscreen: boolean;
  safeTop: number;
  safeBottom: number;
}

/** What the page is about (API chat/page-subject.ts): the chat opens with questions about it. */
export interface PageInfo {
  kind: "unit" | "investment" | "product";
  label: string;
  suggestions: string[] | null;
}

export type ToFrame =
  | ({ type: "open" } & Layout)
  | ({ type: "layout" } & Layout)
  | { type: "closed" }
  /** A button on the page asked a question ("Zapytaj o to mieszkanie"). */
  | { type: "ask"; question: string; ref: string | null }
  /** Answer to context-request: where the visitor is right now. */
  | { type: "context"; id: number; url: string; ref: string | null; page: PageInfo | null }
  /** A single-page site moved to another page without loading it: that page's subject and questions. */
  | { type: "page"; page: PageInfo | null };

export type ToParent =
  | { type: "ready" }
  | { type: "close" }
  | { type: "unread"; count: number }
  /** A link to the client's own site: navigate the page, the chat follows. */
  | { type: "navigate"; url: string }
  | { type: "context-request"; id: number }
  /** Full screen: a field got or lost focus, so the keyboard is coming or going (the page watches the viewport). */
  | { type: "keyboard"; open: boolean }
  /**
   * Full screen: the visitor drags the sheet down by its header. dy is how far
   * the finger went down from where it grabbed, velocity its speed in px/s at
   * the end (positive = down). The page moves the sheet; the frame only measures.
   */
  | { type: "drag"; phase: "move" | "end" | "cancel"; dy: number; velocity: number };

export type Envelope<T> = T & { ns: typeof NS };

export function wrap<T extends object>(message: T): Envelope<T> {
  return { ...message, ns: NS };
}

export function unwrap<T extends { type: string }>(data: unknown): T | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  return record.ns === NS && typeof record.type === "string" ? (record as unknown as T) : null;
}

/** Same site for navigation purposes: www.example.pl and example.pl are one site. */
export function sameSite(a: string, b: string): boolean {
  try {
    const host = (u: string) => new URL(u).hostname.replace(/^www\./, "");
    return host(a) === host(b);
  } catch {
    return false;
  }
}

export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}
